import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Form, Spinner, Alert, Badge } from "react-bootstrap";
import { MapContainer, TileLayer } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import HeatLayer from "./HeatLayer";
import { fetchHeatmapCommandes } from "./DataService";

// Dégradés distincts pour ne pas confondre pickup (où le matériel est chargé) et
// livraison (où le client se fait livrer) — deux signaux commerciaux différents.
const GRADIENT_PICKUP    = { 0.2: "#1e3a8a", 0.5: "#2563eb", 0.8: "#60a5fa", 1: "#bfdbfe" };
const GRADIENT_LIVRAISON = { 0.2: "#7f1d1d", 0.5: "#dc2626", 0.8: "#f87171", 1: "#fecaca" };

// Centre approximatif de la zone d'activité (Côte d'Azur)
const DEFAULT_CENTER = [43.62, 7.05];

function HeatmapCommandesScreen() {
  const [type, setType] = useState("both"); // pickup|livraison|both
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [data, setData] = useState({ pickup: [], livraison: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetchHeatmapCommandes(type, dateFrom || null, dateTo || null)
      .then(setData)
      .catch(() => setError("Impossible de charger les données de la heatmap."))
      .finally(() => setLoading(false));
  }, [type, dateFrom, dateTo]);

  return (
    <Container fluid className="py-4">
      <Row className="mb-3 align-items-center">
        <Col>
          <h4 className="mb-0">Heatmap pickup / livraison</h4>
          <small className="text-muted">
            {type !== "livraison" && <Badge bg="primary" className="me-2">Pickup : {data.pickup.length}</Badge>}
            {type !== "pickup" && <Badge bg="danger">Livraison : {data.livraison.length}</Badge>}
          </small>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm mb-3">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={3}>
              <Form.Label className="small text-muted mb-1">Afficher</Form.Label>
              <Form.Select size="sm" value={type} onChange={(e) => setType(e.target.value)}>
                <option value="both">Pickup + Livraison</option>
                <option value="pickup">Pickup uniquement</option>
                <option value="livraison">Livraison uniquement</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label className="small text-muted mb-1">Depuis le</Form.Label>
              <Form.Control size="sm" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </Col>
            <Col md={3}>
              <Form.Label className="small text-muted mb-1">Jusqu'au</Form.Label>
              <Form.Control size="sm" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </Col>
            <Col md={3}>
              {(dateFrom || dateTo) && (
                <button className="btn btn-sm btn-outline-secondary" onClick={() => { setDateFrom(""); setDateTo(""); }}>
                  Réinitialiser la période
                </button>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0" style={{ position: "relative" }}>
          {loading && (
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ position: "absolute", inset: 0, zIndex: 1000, background: "rgba(255,255,255,0.6)" }}
            >
              <Spinner animation="border" variant="primary" />
            </div>
          )}
          <MapContainer center={DEFAULT_CENTER} zoom={10} style={{ height: "70vh", width: "100%" }}>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            {type !== "livraison" && <HeatLayer points={data.pickup} options={{ gradient: GRADIENT_PICKUP }} />}
            {type !== "pickup" && <HeatLayer points={data.livraison} options={{ gradient: GRADIENT_LIVRAISON }} />}
          </MapContainer>
        </Card.Body>
      </Card>
      <div className="small text-muted mt-2">
        🔵 Pickup (chargement) · 🔴 Livraison (dépose chantier) — commandes annulées exclues.
      </div>
    </Container>
  );
}

export default HeatmapCommandesScreen;
